import { ActionIcon, Group, Tooltip } from '@mantine/core'
import {
  IconPencil,
  IconPlayerPause,
  IconPlayerPlay,
  IconReload,
  IconTrash
} from '@tabler/icons-react'
import { ReactNode } from 'react'
import { FormattedMessage } from 'react-intl'
import { Deployment, DeploymentStatus } from '@/types/deployment'

type ActionButtonProps = {
  tooltip: ReactNode
  icon: ReactNode
  color?: string
  onClick: () => void
}

export const ActionButton = ({
  tooltip,
  icon,
  color,
  onClick
}: ActionButtonProps) => {
  return (
    <Tooltip label={tooltip}>
      <ActionIcon
        variant="outline"
        size="md"
        radius="sm"
        color={color}
        aria-label={typeof tooltip === 'string' ? tooltip : undefined}
        onClick={onClick}
      >
        {icon}
      </ActionIcon>
    </Tooltip>
  )
}

type DeploymentListActionsProps = {
  deployment: Deployment
  canUpdateDeployment: boolean
  canDeleteDeployment: boolean
  onEditDeployment: () => void
  onPauseDeployment: () => void
  onResumeDeployment: () => void
  onRestartDeployment: () => void
  onDeleteDeployment: () => void
}

export const DeploymentListActions = ({
  deployment,
  canUpdateDeployment,
  canDeleteDeployment,
  onEditDeployment,
  onPauseDeployment,
  onResumeDeployment,
  onRestartDeployment,
  onDeleteDeployment
}: DeploymentListActionsProps) => {
  const showRestart =
    canUpdateDeployment &&
    [
      DeploymentStatus.Running,
      DeploymentStatus.Stopped,
      DeploymentStatus.Inactive,
      DeploymentStatus.Failed,
      // A recovering deployment may be crash-looping; let the operator force a
      // restart rather than wait on Docker's auto-restart.
      DeploymentStatus.Recovering
    ].includes(deployment.status)

  return (
    <Group wrap="nowrap" gap={8} justify="end">
      {canUpdateDeployment && (
        <ActionButton
          tooltip={
            <FormattedMessage id="deployments.list.buttons.edit.tooltip" />
          }
          icon={<IconPencil style={{ width: '70%', height: '70%' }} />}
          color="blue"
          onClick={onEditDeployment}
        />
      )}

      {canUpdateDeployment &&
        deployment.status === DeploymentStatus.Running && (
          <ActionButton
            tooltip={
              <FormattedMessage id="deployments.list.buttons.pause.tooltip" />
            }
            icon={<IconPlayerPause style={{ width: '70%', height: '70%' }} />}
            color="yellow"
            onClick={onPauseDeployment}
          />
        )}

      {canUpdateDeployment &&
        deployment.status === DeploymentStatus.Stopped && (
          <ActionButton
            tooltip={
              <FormattedMessage id="deployments.list.buttons.resume.tooltip" />
            }
            icon={<IconPlayerPlay style={{ width: '70%', height: '70%' }} />}
            color="green"
            onClick={onResumeDeployment}
          />
        )}

      {showRestart && (
        <ActionButton
          tooltip={
            <FormattedMessage id="deployments.list.buttons.restart.tooltip" />
          }
          icon={<IconReload style={{ width: '70%', height: '70%' }} />}
          color="orange"
          onClick={onRestartDeployment}
        />
      )}

      {canDeleteDeployment && (
        <ActionButton
          tooltip={
            <FormattedMessage id="deployments.list.buttons.delete.tooltip" />
          }
          icon={<IconTrash style={{ width: '70%', height: '70%' }} />}
          color="red"
          onClick={onDeleteDeployment}
        />
      )}
    </Group>
  )
}
